/**
 * Seam bindings — activation rules stored as applogs (decided: from day one).
 *
 * v1 minimal schema (binding/pattern reserved for pattern-dispatch later):
 *   binding/plugin   URL (or IPFS gateway path) of the plugin module
 *   binding/slot     'kids' (only slot in the PoC)
 *   binding/enabled  boolean (default true)
 */
import { deriveDeterministicEntityID, query } from '@wovin/core'
import { Logger } from 'besonders-logger'
import { getApplogDB, insertApplogs } from '../../data/ApplogDB'
import { addBlock } from '../../data/block-utils'
import { currentOf } from './context'
import { getLoadedSeamPlugins, loadSeamPlugin } from './loader'

const { WARN, DEBUG } = Logger.setup(Logger.DEBUG) // eslint-disable-line unused-imports/no-unused-vars

export const BINDING_AT = {
	plugin: 'binding/plugin',
	slot: 'binding/slot',
	enabled: 'binding/enabled',
} as const

/** Load all enabled plugin bindings from the local thread (called from initPlugins) */
export async function initSeamBindings() {
	const thread = currentOf(getApplogDB())
	const result = query(thread, [{ en: '?binding', at: BINDING_AT.plugin, vl: '?url' }])
	DEBUG(`[seam] bindings found:`, result.records)
	for (const rec of result.records) {
		const url = rec.url as string
		const bindingID = rec.binding as string
		const enabledResult = query(thread, [{ en: bindingID, at: BINDING_AT.enabled, vl: '?enabled' }])
		const enabled = enabledResult.records[0]?.enabled ?? true
		if (!enabled) continue
		try {
			await loadSeamPlugin(url)
		} catch (err) {
			WARN(`[seam] failed to load bound plugin ${url}:`, err)
		}
	}
}

/** Write a binding as applogs (deterministic entity ID → idempotent across devices) */
export function addPluginBinding(url: string, slot = 'kids') {
	const en = deriveDeterministicEntityID(`seam-binding:${url}`)
	insertApplogs(getApplogDB(), [
		{ en, at: BINDING_AT.plugin, vl: url },
		{ en, at: BINDING_AT.slot, vl: slot },
		{ en, at: BINDING_AT.enabled, vl: true },
	])
	return en
}

export function setBindingEnabled(url: string, enabled: boolean) {
	const en = deriveDeterministicEntityID(`seam-binding:${url}`)
	insertApplogs(getApplogDB(), [{ en, at: BINDING_AT.enabled, vl: enabled }])
}

/** Dev helper: seed a demo checklist block (root + kids) for the demo plugin */
export function seedDemoChecklist() {
	const thread = currentOf(getApplogDB())
	const rootID = addBlock({ thread: thread as never, asChildOf: null, content: 'Demo checklist (seam PoC)' })
	insertApplogs(getApplogDB(), [{ en: rootID, at: 'block/type', vl: 'checklist' }])
	for (const item of ['Try the checkbox', 'Watch demo/checked applogs weave', 'Review seam-design.md']) {
		addBlock({ thread: thread as never, asChildOf: rootID, content: item })
	}
	return rootID
}

const DEMO_PLUGIN_URL = '/plugins/demo-checklist.js'

/** window.seam — dev/PoC affordances */
export function exposeSeamDevGlobals() {
	;(window as unknown as { seam: unknown }).seam = {
		load: loadSeamPlugin,
		addBinding: addPluginBinding,
		setEnabled: setBindingEnabled,
		list: () => [...getLoadedSeamPlugins().keys()],
		seedDemo: seedDemoChecklist,
		/** dev helper: create a root block with arbitrary content (e.g. '+todo try me') */
		seedBlock: (content: string) => addBlock({ thread: currentOf(getApplogDB()) as never, asChildOf: null, content }),
		/** one-shot: bind + load the demo plugin, seed a demo block */
		demo: async () => {
			addPluginBinding(DEMO_PLUGIN_URL)
			await loadSeamPlugin(DEMO_PLUGIN_URL)
			return seedDemoChecklist()
		},
	}
}
